import { FC } from "react";
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { usePopover } from "hooks/use-popover";
import { FolderItem } from "../interface";
import { popoverMenuItems } from "../MyTemplateHelper";
import strings from "global/constants/StringConstants";
import { convertESTtoUserLocalDateAndTime, isTruthy } from "helpers/methods";
import { SignRequest } from "models/interfaces";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { setIsRequest } from "redux/appSlice";
import { useFeature, useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import templatesStyles from "../Templates.styles";
import templatesTokens, {
  templatesResponsiveTokens,
  templatesTypeTokens,
} from "../Templates.tokens";
import {
  DocumentGlyph,
  DocumentSmallGlyph,
  FolderGlyph,
  FolderSmallGlyph,
  KebabGlyph,
} from "./TemplatesIcons";
import LibrarySourceBadges, {
  isLibraryKit,
  libraryCardSx,
  librarySubLabel,
} from "../TemplateLibrary/components/LibrarySourceBadges";
import { KitGlyph } from "../TemplateLibrary/TemplateLibraryIcons";

const styles = templatesStyles;
const { card, kebab } = templatesTokens;
const { phone, tablet } = templatesResponsiveTokens;

interface TemplateCardProps {
  item: FolderItem;
  setOpenDialog: (value: boolean) => void;
  setDialogType: (value: string) => void;
  selectedFolder: FolderItem | null;
  setSelectedFolder: (value: FolderItem | null) => void;
  setNewFolderName: (value: string) => void;
  setFolderNameError: (value: string) => void;
  handleFolderClick: (item: FolderItem) => void;
  handleUpdateTemplate: () => void;
  handleUseTemplate: (item?: SignRequest) => void;
  handleCreateWebForm?: () => void;
  isLoading?: boolean;
  isShowMoreIcon?: boolean;
  isUseTemplate?: boolean;
  dispatch: any;
  handleCloseTemplateModal: any;
  /**
   * Phone layout: the reference collapses the tile to a single compact row
   * (icon · name / meta · kebab) whichever view the toggle is on.
   */
  compact?: boolean;
}

/**
 * One folder / template tile in the Templates grid. Chrome, tints and type
 * scale all come from `Templates.tokens.ts` so the card matches
 * `Templates.dc.html` exactly.
 */
export const TemplateGridView: FC<TemplateCardProps> = ({
  item,
  setOpenDialog,
  setDialogType,
  selectedFolder,
  setSelectedFolder,
  setNewFolderName,
  setFolderNameError,
  handleFolderClick,
  handleUpdateTemplate,
  handleUseTemplate,
  handleCreateWebForm,
  isLoading = false,
  isShowMoreIcon = true,
  isUseTemplate = false,
  dispatch,
  handleCloseTemplateModal,
  compact = false,
}) => {
  const popover = usePopover<HTMLElement>();
  const webFormsAvailable = useFeature(FeatureKeys.ENVELOPE_WEB_FORMS);
  const webFormsState = useFeatureState(FeatureKeys.ENVELOPE_WEB_FORMS);
  const webFormsTooltip = webFormsAvailable
    ? ""
    : webFormsState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((webFormsState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : strings.FEATURE_GATE_UPSELL_TOOLTIP;

  const isFolder = item.type === strings.folder;
  const isKit = isLibraryKit(item);
  const tint =
    isFolder || isKit
      ? templatesTypeTokens.folder
      : templatesTypeTokens.template;
  const itemCount = item.itemCount ?? 0;
  const subLabel = isFolder
    ? `${itemCount} ${itemCount === 1 ? "item" : "items"}`
    : (librarySubLabel(item) ?? "Document template");

  const createdAt =
    item.createdOn &&
    convertESTtoUserLocalDateAndTime(
      item.createdOn,
      "MMMM D, YYYY h:mm A",
      "MM/DD/YYYY HH:mm:ss",
    );

  // The compact row puts the stamp on the same line as the item count, so it
  // takes the reference's short form rather than the full timestamp.
  const createdAtShort =
    item.createdOn &&
    convertESTtoUserLocalDateAndTime(
      item.createdOn,
      "MMM D, YYYY",
      "MM/DD/YYYY HH:mm:ss",
    );

  const createdBy = item.requestedBy?.name;

  const folderMenuFunction = (label: string) => {
    setDialogType(label);
    if (label === strings.rename && selectedFolder) {
      setNewFolderName(selectedFolder.templateName ?? "");
    } else if (label === strings.update) {
      handleUpdateTemplate();
    } else if (label === strings.use) {
      handleUseTemplate();
    } else if (label === "Web Form") {
      handleCreateWebForm?.();
      return;
    } else {
      setNewFolderName("");
    }
    setFolderNameError("");
    setOpenDialog(true);
  };

  const isAnyMenuItemAvailable =
    popoverMenuItems(selectedFolder?.type!, webFormsAvailable)?.length > 0;
  const showKebab = isShowMoreIcon && isAnyMenuItemAvailable;

  const openMenuAt = (anchor: HTMLElement) => {
    popover.anchorRef.current = anchor;
    popover.handleOpen();
    setSelectedFolder?.(item);
  };

  const handleCardClick = () => {
    setSelectedFolder?.(item);
    if (!isLoading && isFolder) {
      handleFolderClick(item);
      return;
    }
    if (item.type) {
      const currentMenuItems = popoverMenuItems(item.type, webFormsAvailable);
      if (!currentMenuItems.includes(strings.use)) return;
    }
    if (isUseTemplate) {
      if (!isLoading && item.type === strings.template) {
        handleUseTemplate(item);
      }
      return;
    }
    dispatch(setIsRequest(true));
    history.push({
      pathname: viewpaths.useTemplateViewPath,
      state: item,
    });
    handleCloseTemplateModal?.();
  };

  const nameButtonProps = {
    component: "button" as const,
    type: "button" as const,
    "aria-label": `Open ${item.templateName}`,
    onClick: (event: React.MouseEvent) => {
      event.stopPropagation();
      handleCardClick();
    },
  };

  const cardProps = {
    onClick: handleCardClick,
    onContextMenu: (event: React.MouseEvent) => {
      event.preventDefault();
      if (isLoading || !showKebab) return;
      const anchor = document.createElement("div");
      anchor.style.position = "fixed";
      anchor.style.top = `${event.clientY}px`;
      anchor.style.left = `${event.clientX}px`;
      document.body.appendChild(anchor);
      openMenuAt(anchor);
    },
  };

  const kebabButton = (sx: any, iconSize: any) =>
    showKebab && (
      <IconButton
        sx={sx}
        disabled={isLoading}
        aria-label="More options"
        onClick={(event) => {
          if (isLoading) return;
          event.stopPropagation();
          openMenuAt(event.currentTarget);
        }}
        ref={
          popover.anchorRef as React.MutableRefObject<HTMLButtonElement | null>
        }
      >
        <KebabGlyph sx={iconSize} />
      </IconButton>
    );

  return (
    <>
      {compact ? (
        <Box sx={styles.mobileCard(isLoading)} {...cardProps}>
          <Box sx={styles.mobileIconTile(tint.bg)}>
            {isFolder ? (
              <FolderSmallGlyph
                sx={{
                  width: phone.card.folderIconSize.std,
                  height: phone.card.folderIconSize.std,
                  color: tint.stroke,
                  "@media (max-width:389.98px)": {
                    width: phone.card.folderIconSize.sm,
                    height: phone.card.folderIconSize.sm,
                  },
                }}
              />
            ) : (
              <DocumentSmallGlyph
                sx={{
                  width: phone.card.docIconSize.std,
                  height: phone.card.docIconSize.std,
                  color: tint.stroke,
                  "@media (max-width:389.98px)": {
                    width: phone.card.docIconSize.sm,
                    height: phone.card.docIconSize.sm,
                  },
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography {...nameButtonProps} sx={styles.mobileNameButton}>
              {item.templateName}
            </Typography>
            <Typography component="div" sx={styles.mobileSub}>
              {subLabel}
              {isTruthy(createdAtShort) ? ` · ${createdAtShort}` : ""}
            </Typography>
          </Box>
          {kebabButton(styles.mobileKebabButton, {
            width: phone.card.kebabIconSize.std,
            height: phone.card.kebabIconSize.std,
            "@media (max-width:389.98px)": {
              width: phone.card.kebabIconSize.sm,
              height: phone.card.kebabIconSize.sm,
            },
          })}
        </Box>
      ) : (
        <Box
          sx={[styles.card(isLoading), libraryCardSx(item)] as any}
          {...cardProps}
        >
          <Box
            component="button"
            type="button"
            aria-label={`Open ${item.templateName}`}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              handleCardClick();
            }}
            sx={styles.cardOpenButton}
          />
          <Box sx={styles.cardHead}>
            <Box sx={styles.cardIconTile(tint.bg)}>
              {isFolder ? (
                <FolderGlyph
                  sx={{
                    width: { sm: tablet.card.folderIconSize, md: card.iconSize },
                    height: {
                      sm: tablet.card.folderIconSize,
                      md: card.iconSize,
                    },
                    color: tint.stroke,
                  }}
                />
              ) : isKit ? (
                <KitGlyph
                  sx={{
                    width: { sm: tablet.card.docIconSize, md: card.iconSize },
                    height: { sm: tablet.card.docIconSize, md: card.iconSize },
                    color: tint.stroke,
                  }}
                />
              ) : (
                <DocumentGlyph
                  sx={{
                    width: { sm: tablet.card.docIconSize, md: card.iconSize },
                    height: { sm: tablet.card.docIconSize, md: card.iconSize },
                    color: tint.stroke,
                  }}
                />
              )}
            </Box>
            {kebabButton(styles.cardKebabButton, {
              width: card.kebabIconSize,
              height: card.kebabIconSize,
            })}
          </Box>

          <LibrarySourceBadges item={item} sx={{ mb: "9px" }} />
          <Tooltip
            title={item.templateName ?? ""}
            disableHoverListener={(item.templateName ?? "").length <= 24}
            arrow
            describeChild
          >
            <Typography component="div" sx={styles.cardName}>
              {item.templateName}
            </Typography>
          </Tooltip>
          <Typography component="div" sx={styles.cardSub}>
            {subLabel}
          </Typography>
          {isTruthy(createdBy) && (
            <Typography component="div" sx={styles.cardCreatedBy}>
              Created by {createdBy}
            </Typography>
          )}
          {isTruthy(createdAt) && (
            <Typography component="div" sx={styles.cardCreated}>
              Created on {createdAt}
            </Typography>
          )}
        </Box>
      )}

      <Menu
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        open={popover.open}
        onClick={(event) => event.stopPropagation()}
        onClose={() => {
          popover.handleClose();
          if (
            popover.anchorRef.current &&
            popover.anchorRef.current.parentNode === document.body
          ) {
            document.body.removeChild(popover.anchorRef.current);
          }
        }}
      >
        {popoverMenuItems(selectedFolder?.type!, webFormsAvailable).map(
          (label: string) => {
            const isWebFormLocked = label === "Web Form" && !webFormsAvailable;
            const menuItem = (
              <MenuItem
                key={label}
                disabled={isWebFormLocked}
                onClick={() => {
                  if (isWebFormLocked) return;
                  popover.handleClose();
                  folderMenuFunction(label);
                }}
              >
                {label}
                {isWebFormLocked && (
                  <LockOutlinedIcon
                    sx={{ fontSize: kebab.iconSize, color: "text.secondary", ml: "auto" }}
                  />
                )}
              </MenuItem>
            );
            return isWebFormLocked ? (
              <Tooltip
                key={label}
                title={webFormsTooltip}
                placement="left"
                arrow
                describeChild
              >
                <Box component="span" sx={{ display: "block" }}>
                  {menuItem}
                </Box>
              </Tooltip>
            ) : (
              menuItem
            );
          },
        )}
      </Menu>
    </>
  );
};
